<template>
  <section class="max-w-[1440px] mx-auto px-12 py-12 space-y-12">

    <!-- HEADER -->
    <header>
      <h2 class="text-4xl font-black tracking-tight">
        Market Insights
      </h2>
      <p class="text-slate-400 mt-2 max-w-3xl">
        Aggregated intelligence derived from verified influencer audiences.
        No individual fan or creator-level data is exposed.
      </p>
    </header>

    <!-- KPIs -->
    <section class="grid grid-cols-1 md:grid-cols-4 gap-8">
      <div
        v-for="kpi in kpis"
        :key="kpi.label"
        class="bg-white p-10 rounded-3xl premium-shadow border border-slate-50
               flex flex-col justify-between min-h-[200px]"
      >
        <div>
          <p class="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">
            {{ kpi.label }}
          </p>
          <h2 class="text-5xl font-black tracking-tight">
            {{ kpi.value }}
            <span v-if="kpi.suffix" class="text-xl opacity-30">
              {{ kpi.suffix }}
            </span>
          </h2>
        </div>

        <div
          v-if="kpi.trend"
          class="flex items-center gap-2 text-emerald-600 bg-emerald-50
                 px-3 py-1 rounded-full text-xs font-bold w-fit"
        >
          <i class='bx bx-trending-up text-sm'></i>
          {{ kpi.trend }}
        </div>
      </div>
    </section>

    <!-- INTELLIGENCE GRID -->
    <section class="grid grid-cols-1 lg:grid-cols-12 gap-12">

      <!-- LEFT -->
      <div class="lg:col-span-8 space-y-8">

        <!-- Audience Growth -->
        <div
          class="bg-white p-8 rounded-[2.5rem] premium-shadow border border-slate-50"
        >
          <h3 class="text-xl font-bold mb-6">
            Verified Audience Growth
          </h3>

          <div id="growthChart"></div>

          <p class="text-xs text-slate-400 mt-4">
            Based on aggregated growth across all active influencers.
          </p>
        </div>

        <!-- Interest Distribution -->
        <div
          class="bg-white p-8 rounded-[2.5rem] premium-shadow border border-slate-50"
        >
          <h3 class="text-xl font-bold mb-6">
            Audience Interest Distribution
          </h3>

          <ul class="space-y-4">
            <li
              v-for="i in interests"
              :key="i.name"
              class="flex items-center justify-between"
            >
              <span class="text-sm font-bold text-slate-700">
                {{ i.name }}
              </span>

              <div class="flex items-center gap-4 w-1/2">
                <div class="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div
                    class="bg-slate-900 h-full"
                    :style="{ width: i.percent + '%' }"
                  ></div>
                </div>
                <span class="text-xs font-bold text-slate-400">
                  {{ i.percent }}%
                </span>
              </div>
            </li>
          </ul>
        </div>

      </div>

      <!-- RIGHT -->
      <div class="lg:col-span-4 space-y-8">

        <!-- Platform Mix -->
        <div
          class="bg-white p-8 rounded-[2rem] premium-shadow border border-slate-50"
        >
          <h3 class="text-xl font-bold mb-6">
            Platform Mix
          </h3>

          <ul class="space-y-4">
            <li
              v-for="p in platforms"
              :key="p.name"
              class="flex justify-between"
            >
              <span class="text-sm font-bold text-slate-700">
                {{ p.name }}
              </span>
              <span class="text-xs font-bold text-slate-400">
                {{ p.percent }}%
              </span>
            </li>
          </ul>
        </div>

        <!-- Education & Occupation -->
        <div
          class="bg-white p-8 rounded-[2rem] premium-shadow border border-slate-50"
        >
          <h3 class="text-xl font-bold mb-6">
            Education Snapshot
          </h3>

          <ul class="space-y-3 text-sm">
            <li
              v-for="e in education"
              :key="e.level"
              class="flex justify-between"
            >
              <span class="text-slate-400">{{ e.level }}</span>
              <strong>{{ e.percent }}%</strong>
            </li>
          </ul>
        </div>

      </div>

    </section>

    <!-- DISCLAIMER -->
    <section
      class="bg-slate-50 p-8 rounded-[2rem] text-center text-xs text-slate-400"
    >
      All insights are anonymized and aggregated.
      No individual fan or influencer data is accessible.
    </section>

  </section>
</template>
<script>

import ApexCharts from "apexcharts";
import { loadMarketInsightsApi } from "../apis/agencyinsightapis";

export default {
  name: "InsightsAc",

  data() {
    return {

      user_id: "",

      kpis: [],
      interests: [],
      platforms: [],
      education: [],

      growthChart: null
    };
  },

  async mounted() {

    try {

      const ldataRaw = sessionStorage.getItem("ldata");

      if (ldataRaw && ldataRaw !== "undefined") {

        const ldata = JSON.parse(ldataRaw);

        if (ldata && ldata.user_id) {
          this.user_id = ldata.user_id;
        }
      }

    } catch (e) {
      console.log("Session parse error", e);
    }

    await this.loadInsights();
  },

  methods: {

    async loadInsights() {

      const res = await loadMarketInsightsApi(this.user_id);

      if (res.status) {

        this.kpis       = res.kpis || [];
        this.interests  = res.interests || [];
        this.platforms  = res.platforms || [];
        this.education  = res.education || [];

        this.renderGrowthChart(res.growthChart || []);

      } else {

        // ✅ Safe placeholders

        this.kpis = [
          { label: "Total Influencers", value: "0" },
          { label: "Verified Audience Reach", value: "0", suffix: "M" },
          { label: "Avg Engagement Health", value: "0", suffix: "%" },
          { label: "Active Categories", value: "0" }
        ];

        this.interests = [];
        this.platforms = [];
        this.education = [];

        this.renderGrowthChart([]);
      }
    },

    renderGrowthChart(chartData) {

      if (this.growthChart) {
        this.growthChart.destroy();
      }

      const options = {

        chart: {
          type: "line",
          height: 220,
          toolbar: { show: false }
        },

        series: [{
          name: "Audience Growth",
          data: chartData.length ? chartData : [0,0,0,0,0]
        }],

        stroke: {
          curve: "smooth",
          width: 3
        },

        grid: {
          strokeDashArray: 4
        },

        xaxis: {
          categories: ["Mon", "Tue", "Wed", "Thu", "Fri"]
        }
      };

      this.growthChart = new ApexCharts(
        document.querySelector("#growthChart"),
        options
      );

      this.growthChart.render();
    }
  }
};
</script>

<style scoped>
/* ===================================
   MARKET INSIGHTS – MOBILE PATCH
   =================================== */
@media (max-width: 640px) {

  /* Global padding override (Tailwind px-12) */
  .px-12 {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .py-12 {
    padding-top: 24px !important;
    padding-bottom: 40px !important;
  }

  /* Header typography */
  header h2 {
    font-size: 26px !important;
    line-height: 1.2;
  }

  header p {
    font-size: 14px;
  }

  /* KPI cards */
  .grid.md\\:grid-cols-4 {
    gap: 16px;
  }

  .min-h-\\[200px\\] {
    min-height: auto !important;
  }

  .p-10 {
    padding: 20px !important;
  }

  .text-5xl {
    font-size: 34px !important;
  }

  .text-xl.opacity-30 {
    font-size: 16px !important;
  }

  /* KPI trend pill */
  .bg-emerald-50 {
    font-size: 11px;
    padding: 4px 10px !important;
  }

  /* Intelligence grid spacing */
  .gap-12 {
    gap: 24px !important;
  }

 

  /* Charts */
  svg.h-56 {
    height: 160px !important;
  }


  /* Right column cards */
  .lg\\:col-span-4 .p-8 {
    padding: 20px !important;
  }

  /* Disclaimer */
  .bg-slate-50 {
    padding: 16px !important;
    font-size: 11px;
    line-height: 1.5;
  }
}

</style>